// src/pages/admin/AdminClients.js — Maxula — v1.466.0 / v1.403.0 (suivi, relances, anniversaires)
// AbodyStudio Limited — https://abodystudio.com/ — Support@abodystudio.com
// ============================================================================
// ECRAN CLIENTS — LA FIDELITE, ENFIN VISIBLE
//
// Les fiches se creaient depuis la premiere commande, mais aucun ecran ne les
// montrait. C'est ici qu'on repond a « qui revient, et combien il depense ».
//
// TRI PAR DEFAUT : le total encaisse, decroissant. Pas le nombre de commandes —
// dix petits passages ne pesent pas autant qu'un client a 5 000 MAD, et c'est
// le montant qui justifie une remise.
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FiUsers, FiSearch, FiPhone, FiAward, FiShoppingBag, FiTrendingUp, FiX, FiRefreshCw,
  FiGift, FiSend, FiMail, FiCheckCircle, FiBell, FiEdit2, FiSave, FiMapPin, FiCalendar,
  FiMessageSquare, FiClock, FiUser, FiFileText, FiPlus,
} from 'react-icons/fi';
import { FaBirthdayCake, FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import API from '../../services/api';
import CreateOrderModal from '../../components/orders/CreateOrderModal';

const mad = (v) => `${(Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
const jour = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

// v1.403.0 — suivi client
const TYPES_SUIVI = [
  { key: 'call', label: 'Appel' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
  { key: 'visit', label: 'Visite' },
  { key: 'note', label: 'Note interne' },
];
const RESULTATS = [
  { key: '', label: '— Résultat —' },
  { key: 'reached', label: 'Joint' },
  { key: 'no_answer', label: 'Pas de réponse' },
  { key: 'callback', label: 'À rappeler' },
  { key: 'interested', label: 'Intéressé' },
  { key: 'not_interested', label: 'Pas intéressé' },
  { key: 'ordered', label: 'A commandé' },
  { key: 'other', label: 'Autre' },
];
const LIB_TYPE = Object.fromEntries(TYPES_SUIVI.concat([{ key: 'birthday', label: 'Anniversaire' }, { key: 'reward', label: 'Remise' }, { key: 'order', label: 'Bon de commande' }]).map((t) => [t.key, t.label]));
const LIB_RES = Object.fromEntries(RESULTATS.filter((r) => r.key).map((r) => [r.key, r.label]));
const heure = (d) => (d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const dateInput = (d) => { if (!d) return ''; const x = new Date(d); return Number.isNaN(x.getTime()) ? '' : x.toISOString().slice(0, 10); };
const ageDe = (b) => { if (!b) return null; const [y, m, d] = String(b).slice(0, 10).split('-').map(Number); const n = new Date(); let a = n.getFullYear() - y; if (n.getMonth() + 1 < m || (n.getMonth() + 1 === m && n.getDate() < d)) a -= 1; return a > 0 && a < 120 ? a : null; };

const TRIS = [
  { key: 'spent', label: 'Total dépensé' },
  { key: 'orders', label: 'Nombre de commandes' },
  { key: 'points', label: 'Points' },
  { key: 'recent', label: 'Dernière commande' },
  { key: 'name', label: 'Nom' },
  { key: 'followup', label: 'Prochain rappel' },
];

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [sort, setSort] = useState('spent');
  const [fidelesSeuls, setFidelesSeuls] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fiche, setFiche] = useState(null);
  const [ficheLoading, setFicheLoading] = useState(false);
  // v1.467.0 — envoi d'une remise
  const [fidelite, setFidelite] = useState({ paliers: [], madParPoint: 10 });
  const [remise, setRemise] = useState(null);   // { client, label, message, points, deduct }
  const [remiseBusy, setRemiseBusy] = useState(false);
  // v1.403.0 — suivi
  const [filtre, setFiltre] = useState('');        // '' | followup | birthday | birthweek
  const [mesClients, setMesClients] = useState(false);
  const [agenda, setAgenda] = useState(null);
  const [droits, setDroits] = useState({ contact: true, edit: true, followup: true, commande: false });
  const [edition, setEdition] = useState(null);    // formulaire de la fiche
  const [editBusy, setEditBusy] = useState(false);
  const [suivi, setSuivi] = useState({ type: 'call', outcome: '', comment: '', nextAt: '' });
  const [suiviBusy, setSuiviBusy] = useState(false);
  const [searchParams] = useSearchParams();
  // v1.405.0 — bon de commande depuis la fiche (payé ou en attente)
  const [bonClient, setBonClient] = useState(null);

  // Recherche differee : interroger le serveur a chaque touche saturerait une
  // base deja sous tension aux heures de pointe.
  useEffect(() => {
    const t = setTimeout(() => { setRecherche(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 25, sort };
      if (recherche) params.q = recherche;
      if (fidelesSeuls) params.minOrders = 2;
      if (filtre) params.filter = filtre;
      if (mesClients) params.mine = 1;
      const res = await API.get('/admin/customers', { params });
      const p = res?.data || {};
      if (p.droits) setDroits(p.droits);
      API.get('/admin/customers/agenda', { params: mesClients ? { mine: 1 } : {} })
        .then((r) => setAgenda(r?.data?.data || null)).catch(() => {});
      setClients(Array.isArray(p.data) ? p.data : []);
      setStats(p.stats || null);
      if (p.fidelite) setFidelite(p.fidelite);
      setPages(p.pagination?.pages || 1);
      setTotal(p.pagination?.total || 0);
    } catch (e) {
      const st = e?.response?.status;
      toast.error(st === 503
        ? "Fiches clients indisponibles : déployez customerController.js et Customer.js, puis redémarrez."
        : (e?.response?.data?.message || 'Erreur de chargement'));
      setClients([]);
    } finally { setLoading(false); }
  }, [page, sort, recherche, fidelesSeuls, filtre, mesClients]);

  useEffect(() => { charger(); }, [charger]);

  // Message pre-rempli : le modele du palier, avec le nom et le solde
  // substitues. L'agent peut le modifier avant d'envoyer — un message qu'on ne
  // peut pas adapter finit par ne plus etre envoye du tout.
  const preparerRemise = (c, anniversaire = false) => {
    if (anniversaire) {
      setRemise({
        client: c,
        label: 'Remise anniversaire',
        message: `Joyeux anniversaire ${c.name || ''} ! Toute l'équipe Maxula vous souhaite une belle journée. `
          + 'Pour l\'occasion, profitez d\'une remise sur votre prochaine réservation. À très vite !',
        points: 0,
        deduct: false,
      });
      return;
    }
    const p = c.palier;
    const modele = (p && p.message) || '';
    const texte = modele
      ? modele.replace(/\{nom\}/g, c.name || '').replace(/\{points\}/g, String(c.loyaltyPoints || 0))
      : `Bonjour ${c.name || ''}, merci de votre fidélité chez Maxula ! `
        + `Vous cumulez ${c.loyaltyPoints || 0} points.`;
    setRemise({
      client: c,
      label: (p && p.label) || '',
      message: texte,
      points: p ? p.points : 0,
      deduct: false,
    });
  };

  // WhatsApp depuis le poste de l'agent : il voit le message partir, et aucune
  // configuration serveur n'est necessaire. C'est aussi le canal le plus sur
  // ici, les envois email de la plateforme etant actuellement rejetes par
  // l'hebergeur.
  const ouvrirWhatsApp = (c, texte) => {
    const num = String(c.phone || '').replace(/[^\d]/g, '');
    // wa.me exige l'indicatif pays : un 06 marocain doit devenir 2126...
    const international = num.startsWith('0') ? `212${num.slice(1)}` : num;
    window.open(`https://wa.me/${international}?text=${encodeURIComponent(texte)}`, '_blank', 'noopener');
  };

  const enregistrerRemise = async (canal) => {
    if (!remise) return;
    if (!remise.label.trim()) return toast.error('Indiquez la remise accordée.');
    try {
      setRemiseBusy(true);
      if (canal === 'whatsapp') ouvrirWhatsApp(remise.client, remise.message);
      if (canal === 'email' && remise.client.email) {
        window.open(`mailto:${remise.client.email}?subject=${encodeURIComponent('Votre remise fidélité Maxula')}&body=${encodeURIComponent(remise.message)}`, '_blank');
      }
      await API.post(`/admin/customers/${remise.client.id}/reward`, {
        label: remise.label.trim(), channel: canal,
        points: remise.points, deduct: remise.deduct,
      });
      toast.success('Remise enregistrée');
      const idFiche = fiche?.customer?.id;
      setRemise(null);
      charger();
      if (idFiche && idFiche === remise.client.id) ouvrirFiche({ id: idFiche });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Enregistrement impossible");
    } finally { setRemiseBusy(false); }
  };

  const remplirEdition = (c) => setEdition({
    name: c?.name || '', email: c?.contactMasque ? '' : (c?.email || ''), city: c?.city || '',
    address: c?.contactMasque ? '' : (c?.address || ''), birthDate: c?.birthDate ? String(c.birthDate).slice(0, 10) : '',
    notes: c?.notes || '', ouvert: false,
  });

  const ouvrirFiche = async (c) => {
    setFiche({ customer: c, orders: null, followUps: null });
    remplirEdition(c);
    setSuivi({ type: 'call', outcome: '', comment: '', nextAt: '' });
    try {
      setFicheLoading(true);
      const res = await API.get(`/admin/customers/${c.id}`);
      const d = res?.data?.data || { customer: c, orders: [], followUps: [] };
      setFiche(d);
      remplirEdition(d.customer);
      if (d.droits) setDroits(d.droits);
    } catch (e) {
      setFiche({ customer: c, orders: [], followUps: [] });
    } finally { setFicheLoading(false); }
  };

  // Ouverture directe depuis une notification : /admin/clients?id=12
  useEffect(() => {
    const id = Number(searchParams.get('id'));
    if (id > 0) ouvrirFiche({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enregistrerFiche = async () => {
    if (!fiche?.customer?.id || !edition) return;
    try {
      setEditBusy(true);
      const body = {
        name: edition.name, city: edition.city, birthDate: edition.birthDate, notes: edition.notes,
      };
      // Coordonnees : envoyees seulement si l'agent a le droit de les voir,
      // sinon on ecraserait l'email reel par un champ vide.
      if (!fiche.customer.contactMasque) { body.email = edition.email; body.address = edition.address; }
      const r = await API.put(`/admin/customers/${fiche.customer.id}`, body);
      toast.success(r?.data?.message || 'Fiche mise à jour');
      await ouvrirFiche({ id: fiche.customer.id });
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Enregistrement impossible');
    } finally { setEditBusy(false); }
  };

  const enregistrerSuivi = async () => {
    if (!fiche?.customer?.id) return;
    if (!suivi.comment.trim() && !suivi.outcome && suivi.type === 'note') {
      return toast.error('Écrivez un commentaire ou choisissez un résultat.');
    }
    try {
      setSuiviBusy(true);
      const r = await API.post(`/admin/customers/${fiche.customer.id}/followups`, {
        type: suivi.type, outcome: suivi.outcome || undefined,
        comment: suivi.comment.trim() || undefined, nextAt: suivi.nextAt || undefined,
      });
      toast.success(r?.data?.message || 'Suivi enregistré');
      setSuivi({ type: 'call', outcome: '', comment: '', nextAt: '' });
      await ouvrirFiche({ id: fiche.customer.id });
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Enregistrement impossible');
    } finally { setSuiviBusy(false); }
  };

  const numeroWa = (c) => {
    const num = String(c?.phone || '').replace(/[^\d]/g, '');
    return num.startsWith('0') ? `212${num.slice(1)}` : num;
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2">
        <FiUsers className="text-primary-600" /> Clients
      </h1>
      <p className="text-gray-500 mb-6">
        Un client = un numéro de téléphone. Les formats 06, +212 et 00212 sont reconnus
        automatiquement comme le même client.
      </p>

      {/* Le chiffre qui compte : combien reviennent. */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Clients enregistrés</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Revenus au moins 2 fois</p>
            <p className="text-2xl font-bold text-green-600">{stats.fideles}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Part de clients fidèles</p>
            <p className="text-2xl font-bold text-primary-600">{stats.part}%</p>
          </div>
        </div>
      )}

      {/* v1.403.0 — SUIVI DU JOUR : cliquer une carte filtre la liste */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {[
          { key: 'followup', icon: FiBell, label: 'À relancer aujourd\'hui', n: agenda?.relances, color: 'text-orange-600', ring: 'border-orange-300 bg-orange-50' },
          { key: 'birthday', icon: FaBirthdayCake, label: 'Anniversaires aujourd\'hui', n: agenda?.annivJour, color: 'text-pink-600', ring: 'border-pink-300 bg-pink-50' },
          { key: 'birthweek', icon: FiCalendar, label: 'Anniversaires (7 jours)', n: agenda?.annivSemaine, color: 'text-purple-600', ring: 'border-purple-300 bg-purple-50' },
        ].map((cte) => (
          <button key={cte.key} type="button"
            onClick={() => { setFiltre((f) => (f === cte.key ? '' : cte.key)); setPage(1); if (cte.key === 'followup') setSort('followup'); }}
            className={`text-left rounded-lg shadow p-4 border-2 transition ${filtre === cte.key ? cte.ring : 'bg-white border-transparent hover:border-gray-200'}`}>
            <p className="text-sm text-gray-600 flex items-center gap-2"><cte.icon className={cte.color} /> {cte.label}</p>
            <p className={`text-2xl font-bold ${cte.color}`}>{cte.n ?? '—'}</p>
            {filtre === cte.key && <p className="text-[11px] text-gray-500 mt-1">Filtre actif — cliquer pour retirer</p>}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button type="button" onClick={() => { setMesClients((v) => !v); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border inline-flex items-center gap-1.5 ${
              mesClients ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>
            <FiUser size={12} /> Mes clients uniquement
          </button>
          {!droits.contact && (
            <span className="text-[11px] text-gray-500">Téléphones et emails masqués : permission « Voir téléphone et email » non accordée.</span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, téléphone (06…, +212…), email"
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm">
            {TRIS.map((t) => <option key={t.key} value={t.key}>Trier par : {t.label}</option>)}
          </select>
          <button type="button"
            onClick={() => { setFidelesSeuls((v) => !v); setPage(1); }}
            className={`px-3 py-2.5 rounded-lg text-sm border inline-flex items-center justify-center gap-2 ${
              fidelesSeuls ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>
            <FiAward size={15} /> Fidèles uniquement
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                <th className="px-5 py-3 text-left">Client</th>
                <th className="px-5 py-3 text-right">Commandes</th>
                <th className="px-5 py-3 text-right">Total encaissé</th>
                <th className="px-5 py-3 text-right">Panier moyen</th>
                <th className="px-5 py-3 text-right">Points</th>
                <th className="px-5 py-3 text-left">Palier</th>
                <th className="px-5 py-3 text-left">Dernière</th>
                <th className="px-5 py-3 text-right">Remise</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clients.map((c) => (
                <tr key={c.id} onClick={() => ouvrirFiche(c)}
                  className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-5 py-3">
                    <div className="font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                      {c.name || '—'}
                      {c.annivAujourdhui && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700 text-[10px] font-bold">
                          <FaBirthdayCake size={10} /> Anniversaire
                        </span>
                      )}
                      {c.relanceDue && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">
                          <FiBell size={10} /> À relancer
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <FiPhone size={11} /> {c.phoneAffiche || c.phone}
                    </div>
                    {c.nextFollowUpAt && !c.relanceDue && (
                      <div className="text-[11px] text-gray-400 flex items-center gap-1"><FiClock size={10} /> rappel le {jour(c.nextFollowUpAt)}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="font-semibold">{c.paidOrdersCount}</span>
                    {/* Les commandes non encaissees sont indiquees a part :
                        elles ne comptent ni dans le total ni dans les points. */}
                    {c.ordersCount > c.paidOrdersCount && (
                      <span className="block text-[11px] text-gray-400">
                        {c.ordersCount - c.paidOrdersCount} non encaissée(s)
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-green-700 whitespace-nowrap">{mad(c.paidTotal)}</td>
                  <td className="px-5 py-3 text-right text-gray-600 whitespace-nowrap">{mad(c.panierMoyen)}</td>
                  <td className="px-5 py-3 text-right">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold">
                      <FiAward size={11} /> {c.loyaltyPoints}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {c.palier ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold">
                        <FiGift size={11} /> {c.palier.label}
                      </span>
                    ) : c.pointsManquants != null ? (
                      // « Encore 40 points » : c'est la phrase qui fait revenir
                      // un client, et l'agent peut la lui dire au comptoir.
                      <span className="text-xs text-gray-400">encore {c.pointsManquants} pts</span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{jour(c.lastOrderAt)}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {c.rewardSentAt && (
                      <div className="text-[11px] text-green-600 mb-1 flex items-center justify-end gap-1">
                        <FiCheckCircle size={11} /> {jour(c.rewardSentAt)}
                      </div>
                    )}
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); preparerRemise(c, c.annivAujourdhui); }}
                      className="px-2.5 py-1 rounded-lg border border-primary-200 bg-primary-50 text-primary-700 text-xs font-semibold hover:bg-primary-100 inline-flex items-center gap-1">
                      <FiSend size={12} /> Envoyer
                    </button>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td colSpan="8" className="px-5 py-12 text-center text-gray-400">
                  Aucun client. Lancez <code>clients-test</code> puis <code>clients</code> dans
                  cPanel pour reconstruire les fiches depuis vos commandes existantes.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">{total} client(s)</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 rounded border border-gray-300 text-sm disabled:opacity-40">Précédent</button>
            <span className="px-3 py-1.5 text-sm">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded border border-gray-300 text-sm disabled:opacity-40">Suivant</button>
          </div>
        </div>
      )}

      {/* ============================================================
          ENVOI D'UNE REMISE
          L'envoi part du poste de l'agent (WhatsApp ou messagerie), pas du
          serveur : il voit le message partir. L'application, elle, enregistre
          la trace — sans quoi on ne saurait pas qui a déjà reçu son geste.
         ============================================================ */}
      {remise && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !remiseBusy && setRemise(null)} />
          <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Accorder une remise</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {remise.client.name || '—'} · {remise.client.phoneAffiche} ·{' '}
                <span className="text-amber-700 font-semibold">{remise.client.loyaltyPoints} points</span>
              </p>
            </div>

            <div className="p-6 space-y-4">
              {remise.client.rewardSentAt && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                  Ce client a déjà reçu une remise le <strong>{jour(remise.client.rewardSentAt)}</strong>
                  {remise.client.rewardLabel ? <> : {remise.client.rewardLabel}</> : null}.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remise accordée</label>
                <input type="text" value={remise.label} maxLength={160}
                  onChange={(e) => setRemise((r) => ({ ...r, label: e.target.value }))}
                  placeholder="Ex : 10% sur la prochaine réservation"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400" />
                {fidelite.paliers?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {fidelite.paliers.map((p) => (
                      <button key={p.points} type="button"
                        onClick={() => setRemise((r) => ({ ...r, label: p.label, points: p.points }))}
                        className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
                        {p.points} pts — {p.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message au client</label>
                <textarea rows={4} value={remise.message} maxLength={600}
                  onChange={(e) => setRemise((r) => ({ ...r, message: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400" />
              </div>

              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={remise.deduct}
                  onChange={(e) => setRemise((r) => ({ ...r, deduct: e.target.checked }))}
                  className="w-4 h-4 mt-0.5 rounded border-gray-300 text-pink-500 focus:ring-pink-400" />
                <span className="text-sm text-gray-700">
                  Déduire {remise.points || 0} points du solde
                  <span className="block text-xs text-gray-400">
                    Décoché, les points restent un cumul de référence et ne se consomment pas.
                  </span>
                </span>
              </label>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-wrap justify-end gap-3">
              <button onClick={() => setRemise(null)} disabled={remiseBusy}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100">
                Annuler
              </button>
              {remise.client.email && (
                <button onClick={() => enregistrerRemise('email')} disabled={remiseBusy}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-100 inline-flex items-center gap-2">
                  <FiMail size={15} /> Par email
                </button>
              )}
              <button onClick={() => enregistrerRemise('whatsapp')} disabled={remiseBusy}
                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60 inline-flex items-center gap-2">
                <FiSend size={15} /> {remiseBusy ? 'Envoi…' : 'WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fiche détaillée — v1.403.0 : coordonnées modifiables, suivi des relances */}
      {fiche && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setFiche(null)} />
          <div className="relative bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                  {fiche.customer?.name || 'Client'}
                  {fiche.customer?.annivAujourdhui && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 text-xs font-bold">
                      <FaBirthdayCake size={11} /> Anniversaire aujourd&apos;hui
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-500">{fiche.customer?.phoneAffiche || fiche.customer?.phone}</p>
                {fiche.customer?.staffName && (
                  <p className="text-xs text-gray-400">Client acquis par {fiche.customer.staffName}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {fiche.customer?.phone && (
                  <>
                    <a href={`tel:${fiche.customer.phone}`} className="px-3 py-1.5 rounded-lg border text-sm font-semibold text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5">
                      <FiPhone size={13} /> Appeler
                    </a>
                    <a href={`https://wa.me/${numeroWa(fiche.customer)}`} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 inline-flex items-center gap-1.5">
                      <FaWhatsapp size={14} /> WhatsApp
                    </a>
                  </>
                )}
                {droits.commande && fiche.customer?.id && (
                  <button type="button" onClick={() => setBonClient(fiche.customer)}
                    className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 inline-flex items-center gap-1.5">
                    <FiFileText size={13} /> Bon de commande
                  </button>
                )}
                <button onClick={() => setFiche(null)} className="text-gray-400 hover:text-gray-600 ml-1"><FiX size={20} /></button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto space-y-5">
              {fiche.customer?.annivAujourdhui && (
                <div className="rounded-lg border border-pink-200 bg-pink-50 p-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm text-pink-800 flex items-center gap-2">
                    <FaBirthdayCake /> C&apos;est l&apos;anniversaire de {fiche.customer?.name || 'ce client'}
                    {ageDe(fiche.customer?.birthDate) ? ` (${ageDe(fiche.customer.birthDate)} ans)` : ''} : offrez-lui une remise.
                  </div>
                  <button type="button" onClick={() => preparerRemise(fiche.customer, true)}
                    className="px-3 py-1.5 rounded-lg bg-pink-600 text-white text-sm font-semibold hover:bg-pink-700 inline-flex items-center gap-1.5">
                    <FiGift size={13} /> Offrir une remise
                  </button>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <FiShoppingBag className="mx-auto text-gray-400 mb-1" size={16} />
                  <div className="text-xl font-bold">{fiche.customer?.paidOrdersCount ?? 0}</div>
                  <div className="text-xs text-gray-500">commandes payées</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <FiTrendingUp className="mx-auto text-gray-400 mb-1" size={16} />
                  <div className="text-xl font-bold text-green-700">{mad(fiche.customer?.paidTotal)}</div>
                  <div className="text-xs text-gray-500">total encaissé</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
                  <FiAward className="mx-auto text-amber-500 mb-1" size={16} />
                  <div className="text-xl font-bold text-amber-700">{fiche.customer?.loyaltyPoints ?? 0}</div>
                  <div className="text-xs text-amber-600">points</div>
                </div>
              </div>

              {/* ---- INFORMATIONS ---- */}
              <div className="border border-gray-200 rounded-lg">
                <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
                  <h4 className="font-semibold text-sm">Informations</h4>
                  {droits.edit && edition && !edition.ouvert && (
                    <button type="button" onClick={() => setEdition((e) => ({ ...e, ouvert: true }))}
                      className="text-xs font-semibold text-primary-700 inline-flex items-center gap-1"><FiEdit2 size={12} /> Modifier</button>
                  )}
                </div>
                {edition && edition.ouvert ? (
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <label className="block">Nom
                      <input value={edition.name} onChange={(e) => setEdition({ ...edition, name: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" />
                    </label>
                    <label className="block">Date d&apos;anniversaire
                      <input type="date" value={edition.birthDate} max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setEdition({ ...edition, birthDate: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" />
                    </label>
                    {fiche.customer?.contactMasque ? (
                      <p className="sm:col-span-2 text-xs text-gray-500">Email et adresse : non modifiables sans la permission « Voir téléphone et email ».</p>
                    ) : (
                      <>
                        <label className="block">Email
                          <input type="email" value={edition.email} onChange={(e) => setEdition({ ...edition, email: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="client@exemple.com" />
                        </label>
                        <label className="block">Ville
                          <input value={edition.city} onChange={(e) => setEdition({ ...edition, city: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" />
                        </label>
                        <label className="block sm:col-span-2">Adresse
                          <input value={edition.address} onChange={(e) => setEdition({ ...edition, address: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="Rue, quartier…" />
                        </label>
                      </>
                    )}
                    <label className="block sm:col-span-2">Note / commentaire
                      <textarea rows={3} value={edition.notes} onChange={(e) => setEdition({ ...edition, notes: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="Préférences, remarques…" />
                    </label>
                    <p className="sm:col-span-2 text-[11px] text-gray-400">Le téléphone n&apos;est pas modifiable : c&apos;est l&apos;identifiant du client (un numéro = une fiche).</p>
                    <div className="sm:col-span-2 flex justify-end gap-2">
                      <button type="button" onClick={() => remplirEdition(fiche.customer)} className="px-4 py-2 rounded-lg border text-sm">Annuler</button>
                      <button type="button" onClick={enregistrerFiche} disabled={editBusy}
                        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <FiSave size={13} /> {editBusy ? 'Enregistrement…' : 'Enregistrer'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-700"><FiMail className="text-gray-400" size={13} /> {fiche.customer?.email || <span className="text-gray-400">Email non renseigné</span>}</div>
                    <div className="flex items-center gap-2 text-gray-700"><FaBirthdayCake className="text-gray-400" size={12} />
                      {fiche.customer?.birthDate
                        ? <>{new Date(`${String(fiche.customer.birthDate).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}{ageDe(fiche.customer.birthDate) ? ` · ${ageDe(fiche.customer.birthDate)} ans` : ''}</>
                        : <span className="text-gray-400">Anniversaire non renseigné</span>}
                    </div>
                    <div className="flex items-center gap-2 text-gray-700 sm:col-span-2"><FiMapPin className="text-gray-400" size={13} />
                      {[fiche.customer?.address, fiche.customer?.city].filter(Boolean).join(', ') || <span className="text-gray-400">Adresse non renseignée</span>}
                    </div>
                    {fiche.customer?.notes && (
                      <div className="sm:col-span-2 bg-yellow-50 border border-yellow-200 rounded p-2 text-gray-700 whitespace-pre-line">{fiche.customer.notes}</div>
                    )}
                    <div className="sm:col-span-2 text-xs text-gray-500">
                      Client depuis le <strong>{jour(fiche.customer?.firstOrderAt)}</strong> · dernière commande le <strong>{jour(fiche.customer?.lastOrderAt)}</strong>
                      {fiche.customer?.lastContactAt ? <> · dernier contact le <strong>{jour(fiche.customer.lastContactAt)}</strong></> : null}
                    </div>
                  </div>
                )}
              </div>

              {/* ---- SUIVI / RELANCES ---- */}
              <div className="border border-gray-200 rounded-lg">
                <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
                  <h4 className="font-semibold text-sm flex items-center gap-1.5"><FiMessageSquare size={13} /> Suivi et relances</h4>
                  {fiche.customer?.nextFollowUpAt && (
                    <span className={`text-xs font-semibold inline-flex items-center gap-1 ${fiche.customer.relanceDue ? 'text-orange-600' : 'text-gray-500'}`}>
                      <FiBell size={11} /> Rappel le {jour(fiche.customer.nextFollowUpAt)}
                      {fiche.customer.nextFollowUpByName ? ` (${fiche.customer.nextFollowUpByName})` : ''}
                    </span>
                  )}
                </div>

                {droits.followup && (
                  <div className="p-4 border-b border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                    <select value={suivi.type} onChange={(e) => setSuivi({ ...suivi, type: e.target.value })} className="border rounded-lg px-2 py-2">
                      {TYPES_SUIVI.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                    <select value={suivi.outcome} onChange={(e) => setSuivi({ ...suivi, outcome: e.target.value })} className="border rounded-lg px-2 py-2">
                      {RESULTATS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      Rappel le
                      <input type="date" value={suivi.nextAt} min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setSuivi({ ...suivi, nextAt: e.target.value })} className="flex-1 border rounded-lg px-2 py-1.5 text-sm" />
                    </label>
                    <textarea rows={2} value={suivi.comment} onChange={(e) => setSuivi({ ...suivi, comment: e.target.value })}
                      placeholder="Ce qui a été dit, ce qui est prévu…" className="sm:col-span-3 border rounded-lg px-3 py-2" />
                    <div className="sm:col-span-3 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-gray-400">Un contact sans nouvelle date clôt le rappel en cours.</span>
                      <button type="button" onClick={enregistrerSuivi} disabled={suiviBusy}
                        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <FiSave size={13} /> {suiviBusy ? 'Enregistrement…' : 'Enregistrer le suivi'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="p-4">
                  {ficheLoading || fiche.followUps === null ? (
                    <p className="text-sm text-gray-400 flex items-center gap-2"><FiRefreshCw className="animate-spin" size={13} /> Chargement…</p>
                  ) : (fiche.followUps || []).length ? (
                    <ol className="space-y-3">
                      {fiche.followUps.map((f) => (
                        <li key={f.id} className="border-l-2 border-primary-200 pl-3">
                          <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-2">
                            <span className="font-semibold text-gray-800">{LIB_TYPE[f.type] || f.type}</span>
                            {f.outcome && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{LIB_RES[f.outcome] || f.outcome}</span>}
                            <span>{heure(f.createdAt)}</span>
                            {f.auteur && <span>· par {f.auteur}</span>}
                          </div>
                          {f.comment && <p className="text-sm text-gray-700 whitespace-pre-line mt-0.5">{f.comment}</p>}
                          {f.nextAt && <p className="text-[11px] text-orange-600 mt-0.5 flex items-center gap-1"><FiBell size={10} /> Rappel programmé le {jour(f.nextAt)}</p>}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-gray-400">Aucune relance enregistrée pour ce client.</p>
                  )}
                </div>
              </div>

              {/* ---- HISTORIQUE DES COMMANDES ---- */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm">Commandes</h4>
                  {droits.commande && fiche.customer?.id && (
                    <button type="button" onClick={() => setBonClient(fiche.customer)}
                      className="text-xs font-semibold text-primary-700 hover:underline inline-flex items-center gap-1">
                      <FiPlus size={12} /> Nouveau bon de commande
                    </button>
                  )}
                </div>
                {ficheLoading || fiche.orders === null ? (
                  <p className="text-sm text-gray-400 flex items-center gap-2"><FiRefreshCw className="animate-spin" size={13} /> Chargement…</p>
                ) : fiche.orders.length ? (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {fiche.orders.map((o) => (
                      <div key={o.id} className="px-4 py-2 flex items-center justify-between text-sm">
                        <div>
                          <div className="font-medium text-gray-800">#{o.orderNumber}</div>
                          <div className="text-xs text-gray-400">{jour(o.createdAt)}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{mad(o.total)}</div>
                          <div className={`text-xs ${o.paymentStatus === 'completed' ? 'text-green-600' : 'text-gray-400'}`}>
                            {o.paymentStatus === 'completed' ? 'Payée'
                              : o.status === 'cancelled' ? 'Annulée'
                              : o.paymentStatus === 'failed' ? 'Échouée'
                              : 'En attente de paiement'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Aucune commande rattachée.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {bonClient && (
        <div className="relative z-[70]">
          <CreateOrderModal
            scope="admin"
            initial={{
              customerId: bonClient.id,
              contactMasque: !!bonClient.contactMasque,
              phoneAffiche: bonClient.phoneAffiche,
              emailAffiche: bonClient.contactMasque ? bonClient.email : '',
              customer: {
                name: bonClient.name || '',
                phone: bonClient.contactMasque ? '' : (bonClient.phone || ''),
                email: bonClient.contactMasque ? '' : (bonClient.email || ''),
              },
            }}
            onClose={() => setBonClient(null)}
            onCreated={() => { ouvrirFiche({ id: bonClient.id }); charger(); }}
          />
        </div>
      )}
    </div>
  );
}
